import { LocationItem, SearchData } from '../../../types/search';
import { segmentsToPathArray } from '../utils/segmentHelpers';

interface CategoryItem {
  category_ID: string;
  category_Name_TH: string;
  category_Name_EN: string;
  category_Name_CN: string;
}

interface ActionItem {
  id: string;
  action: string;
}

// Location type labels mapping for multilingual support
const LOCATION_TYPE_LABELS = {
  10: { th: 'รถไฟฟ้า', en: 'Mass Transit', cn: '地铁站' },
  11: { th: 'ห้างสรรพสินค้า', en: 'Shopping Mall', cn: '商场' },
  17: { th: 'มหาวิทยาลัย', en: 'University', cn: '大学' },
  20: { th: 'ถนน', en: 'Road', cn: '路' },
  9: { th: 'โรงพยาบาล', en: 'Hospital', cn: '医院' },
  8: { th: 'สวนสาธารณะ', en: 'Public Park', cn: '公园' },
  1: { th: 'สนามบิน', en: 'Airport', cn: '机场' },
  2: { th: 'สถานที่ยอดนิยม', en: 'Popular Area', cn: '热门地区' }
};

// Helper functions for URL formatting
const getLocationTypeLabel = (typeId: number, locale: string): string => {
  const labels = LOCATION_TYPE_LABELS[typeId];
  if (!labels) return '';
  
  switch (locale) {
    case 'en': return labels.en;
    case 'cn': return labels.cn;
    default: return labels.th;
  }
};

const formatTrainLine = (lineName: string): string => {
  // Convert train line name to URL-friendly format
  // "MRT - สายสีน้ำเงิน" -> "mrt-สายสีน้ำเงิน"
  return lineName.toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/^(bts|mrt|arl|srt)\s*-?\s*/i, '$1-');
};

const formatStationName = (stationName: string): string => {
  // Convert station name to URL-friendly format
  // "MRT เตาปูน BL10" -> "mrt-เตาปูน-bl10"
  return stationName.toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/^(bts|mrt|arl|srt)\s*/i, '$1-');
};

const formatLocationName = (locationName: string): string => {
  // General location name formatting
  return locationName.toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^\w\u0E00-\u0E7F\u4E00-\u9FFF-]/g, '');
};

export class UrlService {
  static generateSearchUrl(
    searchData: SearchData,
    filteredActions: ActionItem[],
    filteredCategories: CategoryItem[],
    locationTypeId: number | null,
    locale: string,
    t: (key: string) => string
  ): string {
    const { 
      transactionType, 
      propertyType, 
      searchText, 
      priceMin, 
      priceMax, 
      location: selectedLocation, 
      subLocation: selectedSubLocation,
      locationSegments,
      selectedBedrooms,
      selectedBathrooms,
      selectedQuota,
      selectedStatus,
      selectedFloor,
      selectedView,
      selectedDirection,
      selectedPublishedBy,
      selectedPostedDate,
      areaMin,
      areaMax,
      selectedDynamicFilters,
      apiQueryParams
    } = searchData;
    
    const urlParts: string[] = [];
    const queryParams: string[] = [];

    if (transactionType) {
      const actionObj = filteredActions.find(action => action.action === transactionType);
      
      if (actionObj) {
        urlParts.push(actionObj.action);
      } else {
        urlParts.push(transactionType);
      }
    } else {
      urlParts.push(t('estate'));
    }

    if (propertyType) {
      const categoryObj = filteredCategories.find(category => {
        if (locale === 'en') return category.category_Name_EN === propertyType;
        if (locale === 'cn') return category.category_Name_CN === propertyType;
        return category.category_Name_TH === propertyType;
      });
      
      if (categoryObj) {
        if (locale === 'en') {
          urlParts.push(categoryObj.category_Name_EN);
        } else if (locale === 'cn') {
          urlParts.push(categoryObj.category_Name_CN);
        } else {
          urlParts.push(categoryObj.category_Name_TH);
        }
      } else if (propertyType === t('all')) {
        urlParts.push('ทั้งหมด');
      } else {
        urlParts.push(propertyType);
      }
    } else {
      urlParts.push('ทั้งหมด');
    }

    if (locationSegments && locationSegments.length > 0) {
      const locationPaths = segmentsToPathArray(locationSegments, locale);
      
      const validPaths = locationPaths.filter(path => path && path.trim().length > 0);
      
      if (validPaths.length > 0) {
        urlParts.push(...validPaths);
      } else {
        console.warn('All locationSegments produced invalid paths, falling back to legacy location handling');
        if (selectedLocation) {
          this.addLegacyLocationPaths(urlParts, queryParams, selectedLocation, selectedSubLocation, locationTypeId, searchText, locale, t);
        }
      }
    } else if (selectedLocation) {
      this.addLegacyLocationPaths(urlParts, queryParams, selectedLocation, selectedSubLocation, locationTypeId, searchText, locale, t);
    }

    if (priceMin && priceMin > 0) {
      queryParams.push(`startPrice=${priceMin}`);
    }

    if (priceMax && priceMax < 10000000) {
      queryParams.push(`endPrice=${priceMax}`);
    }

    if (areaMin && areaMin > 0) {
      queryParams.push(`areaMin=${areaMin}`);
    }

    if (areaMax && areaMax < 1000) {
      queryParams.push(`areaMax=${areaMax}`);
    }

    if (selectedBedrooms) {
      queryParams.push(`bedrooms=${encodeURIComponent(selectedBedrooms)}`);
    }

    if (selectedBathrooms) {
      queryParams.push(`bathrooms=${encodeURIComponent(selectedBathrooms)}`);
    }

    if (selectedQuota) {
      queryParams.push(`quota=${encodeURIComponent(selectedQuota)}`);
    }

    if (selectedStatus) {
      queryParams.push(`status=${encodeURIComponent(selectedStatus)}`);
    }

    if (selectedFloor) {
      queryParams.push(`floor=${encodeURIComponent(selectedFloor)}`);
    }

    if (selectedView) {
      queryParams.push(`view=${encodeURIComponent(selectedView)}`);
    }

    if (selectedDirection) {
      queryParams.push(`direction=${encodeURIComponent(selectedDirection)}`);
    }

    if (selectedPublishedBy) {
      queryParams.push(`publishedBy=${encodeURIComponent(selectedPublishedBy)}`);
    }

    if (selectedPostedDate) {
      queryParams.push(`postedDate=${encodeURIComponent(selectedPostedDate)}`);
    }

    if (apiQueryParams && Object.keys(apiQueryParams).length > 0) {
      Object.entries(apiQueryParams).forEach(([key, value]) => {
        if (value !== null && value !== undefined && value !== '') {
          queryParams.push(`${encodeURIComponent(key)}=${encodeURIComponent(value)}`);
        }
      });
    }

    if (selectedDynamicFilters) {
      Object.entries(selectedDynamicFilters).forEach(([groupId, optionId]) => {
        const hasApiParam = apiQueryParams && Object.keys(apiQueryParams).some(key => 
          key.includes(groupId) || key.includes(optionId.toString())
        );
        
        if (!hasApiParam) {
          queryParams.push(`dynamicFilter_${groupId}=${optionId}`);
        }
      });
    }

    const url = urlParts.join('/') + (queryParams.length > 0 ? '?' + queryParams.join('&') : '');
    
    return url;
  }

  static navigateToUrl(url: string): void {
    if (typeof window !== 'undefined') {
      // ใช้ window.location.replace เพื่อ replace URL แทนการ append
      // หรือใช้ pushState เพื่อ navigate โดยไม่ reload หน้า
      
      // Method 1: Replace current URL (recommended for search)
      window.location.replace(url);
      
      // Method 2: Alternative using pushState (uncomment if needed)
      // window.history.pushState(null, '', url);
      // window.location.reload();
    }
  }

  // Legacy location handling for backward compatibility
  private static addLegacyLocationPaths(
    urlParts: string[],
    queryParams: string[],
    selectedLocation: LocationItem,
    selectedSubLocation: LocationItem | null,
    locationTypeId: number | null,
    searchText: string,
    locale: string,
    t: (key: string) => string
  ): void {
    if (locationTypeId === -1) {
      // Project name
      urlParts.push(t('project_name'));
      urlParts.push(selectedLocation.originalLabel || selectedLocation.label);

      if (selectedLocation.value) {
        queryParams.push(`projectid=${selectedLocation.value}`);
      }
    } else if (locationTypeId === 0) {
      // Province/District/Sub-district hierarchy
      if (searchText && searchText.includes(' - ')) {
        const parts = searchText.split(' - ');
        parts.forEach(p => urlParts.push(p.trim()));
      } else {
        urlParts.push(selectedLocation.label);
        if (selectedSubLocation &&
          selectedSubLocation.value &&
          !selectedSubLocation.value.toString().includes('all_')) {
          urlParts.push(selectedSubLocation.label);
        }
      }
    } else if (locationTypeId === 10) {
      // Mass Transit (รถไฟฟ้า)
      urlParts.push(getLocationTypeLabel(10, locale));
      urlParts.push(formatTrainLine(selectedLocation.label));
      if (selectedSubLocation &&
        selectedSubLocation.value &&
        !selectedSubLocation.value.toString().includes('all_')) {
        urlParts.push(formatStationName(selectedSubLocation.label));
      }
    } else if (locationTypeId === 11) {
      // Shopping Mall (ห้างสรรพสินค้า)
      urlParts.push(getLocationTypeLabel(11, locale));
      urlParts.push(formatLocationName(selectedLocation.label));
    } else if (locationTypeId === 17) {
      // University (มหาวิทยาลัย)
      urlParts.push(getLocationTypeLabel(17, locale));
      urlParts.push(formatLocationName(selectedLocation.label));
    } else if (locationTypeId === 20) {
      // Road (ถนน)
      urlParts.push(getLocationTypeLabel(20, locale));
      urlParts.push(formatLocationName(selectedLocation.label));
    } else if (locationTypeId === 9) {
      // Hospital (โรงพยาบาล)
      urlParts.push(getLocationTypeLabel(9, locale));
      urlParts.push(formatLocationName(selectedLocation.label));
    } else if (locationTypeId === 8) {
      // Public Park (สวนสาธารณะ)
      urlParts.push(getLocationTypeLabel(8, locale));
      urlParts.push(formatLocationName(selectedLocation.label));
    } else if (locationTypeId === 1) {
      // Airport (สนามบิน)
      urlParts.push(getLocationTypeLabel(1, locale));
      urlParts.push(formatLocationName(selectedLocation.label));
    } else if (locationTypeId === 2) {
      // Popular Location (สถานที่ยอดนิยม)
      urlParts.push(getLocationTypeLabel(2, locale));
      urlParts.push(formatLocationName(selectedLocation.label));
      if (selectedSubLocation &&
        selectedSubLocation.value &&
        !selectedSubLocation.value.toString().includes('all_')) {
        urlParts.push(formatLocationName(selectedSubLocation.label));
      }
    } else {
      // Default fallback for unknown location types
      urlParts.push(selectedLocation.label);
      if (selectedSubLocation &&
        selectedSubLocation.value &&
        !selectedSubLocation.value.toString().includes('all_')) {
        urlParts.push(selectedSubLocation.label);
      }
    }
  }

  // Reverse URL parsing functionality
  static parseUrlToSearchData(
    url: string,
    filteredActions: ActionItem[],
    filteredCategories: CategoryItem[],
    locale: string,
    t: (key: string) => string
  ): Partial<SearchData> {
    try {
      const urlObj = new URL(url);
      const pathname = urlObj.pathname;
      const queryParams = urlObj.searchParams;
      
      console.log('UrlService.parseUrlToSearchData:', {
        url,
        pathname,
        locale,
        filteredActions: filteredActions.map(a => ({ id: a.id, action: a.action })),
        filteredCategories: filteredCategories.map(c => ({ 
          id: c.category_ID, 
          th: c.category_Name_TH, 
          en: c.category_Name_EN, 
          cn: c.category_Name_CN 
        }))
      });
      
      const searchData: Partial<SearchData> = {};
      
      // Remove locale prefix if present
      let cleanPath = pathname;
      if (cleanPath.startsWith('/en/')) {
        cleanPath = cleanPath.substring(3);
      } else if (cleanPath.startsWith('/cn/')) {
        cleanPath = cleanPath.substring(3);
      } else if (cleanPath.startsWith('/th/')) {
        cleanPath = cleanPath.substring(3);
      }
      
      // Remove leading and trailing slashes
      cleanPath = cleanPath.replace(/^\/+|\/+$/g, '');
      
      // Split path into parts and decode each segment
      const pathParts = cleanPath ? cleanPath.split('/').map(part => {
        try {
          return decodeURIComponent(part);
        } catch (e) {
          return part;
        }
      }) : [];
      
      console.log('UrlService.parseUrlToSearchData - Path processing:', {
        originalPath: pathname,
        cleanPath,
        pathParts
      });
      
      // Parse path parts
      if (pathParts.length >= 2) {
        // First part: transaction type
        const transactionPart = pathParts[0];
        console.log('UrlService.parseUrlToSearchData - Looking for action:', transactionPart);
        
        const matchedAction = this.findActionByName(transactionPart, filteredActions, locale);
        console.log('UrlService.parseUrlToSearchData - Found action:', matchedAction);
        
        if (matchedAction) {
          searchData.transactionType = matchedAction.action;
        }
        
        // Second part: property type
        const propertyPart = pathParts[1];
        console.log('UrlService.parseUrlToSearchData - Looking for category:', propertyPart);
        
        const matchedCategory = this.findCategoryByName(propertyPart, filteredCategories, locale);
        console.log('UrlService.parseUrlToSearchData - Found category:', matchedCategory);
        
        if (matchedCategory) {
          if (locale === 'en') {
            searchData.propertyType = matchedCategory.category_Name_EN;
          } else if (locale === 'cn') {
            searchData.propertyType = matchedCategory.category_Name_CN;
          } else {
            searchData.propertyType = matchedCategory.category_Name_TH;
          }
        }
        
        // Remaining parts: location segments
        if (pathParts.length > 2) {
          const locationParts = pathParts.slice(2);
          searchData.locationSegments = this.reconstructLocationSegments(locationParts, locale);
          
          // Set searchText based on segments
          if (searchData.locationSegments && searchData.locationSegments.length > 0) {
            const segmentLabels = searchData.locationSegments.map(seg => 
              seg.label[locale as keyof typeof seg.label] || seg.label.th
            );
            searchData.searchText = segmentLabels.join(' - ');
          }
        }
      }
      
      // Parse query parameters
      this.parseQueryParameters(queryParams, searchData);
      
      console.log('UrlService.parseUrlToSearchData - Final result:', searchData);
      
      return searchData;
    } catch (error) {
      console.error('Error parsing URL to SearchData:', error);
      return {};
    }
  }

  // Helper function to find action by name
  private static findActionByName(
    actionName: string,
    filteredActions: ActionItem[],
    locale: string
  ): ActionItem | null {
    const normalizedActionName = actionName.toLowerCase();
    
    return filteredActions.find(action => {
      const normalizedAction = action.action.toLowerCase();
      
      // Direct match
      if (normalizedAction === normalizedActionName) {
        return true;
      }
      
      // Check for partial matches (e.g., "ให้เช่า" contains "เช่า")
      if (normalizedActionName.includes(normalizedAction) || normalizedAction.includes(normalizedActionName)) {
        return true;
      }
      
      // Common translations - bidirectional mapping
      const translations = {
        'ขาย': ['sell', 'sale', '出售'],
        'เช่า': ['rent', 'rental', '出租', 'ให้เช่า'], // Add "ให้เช่า" variation
        'ให้เช่า': ['rent', 'rental', '出租', 'เช่า'], // Add reverse mapping
        'รวม': ['all', 'estate', '全部']
      };
      
      // Check if actionName matches any translation of the current action
      const actionTranslations = translations[action.action] || [];
      if (actionTranslations.some(trans => trans.toLowerCase() === normalizedActionName)) {
        return true;
      }
      
      // Check if current action matches any translation of the actionName
      const nameTranslations = translations[actionName] || [];
      if (nameTranslations.some(trans => trans.toLowerCase() === normalizedAction)) {
        return true;
      }
      
      return false;
    }) || null;
  }

  // Helper function to find category by name
  private static findCategoryByName(
    categoryName: string,
    filteredCategories: CategoryItem[],
    locale: string
  ): CategoryItem | null {
    const normalizedCategoryName = categoryName.toLowerCase();
    
    return filteredCategories.find(category => {
      // Check all language variants
      const namesToCheck = [
        category.category_Name_TH?.toLowerCase(),
        category.category_Name_EN?.toLowerCase(),
        category.category_Name_CN?.toLowerCase()
      ].filter(Boolean);
      
      // Direct match
      if (namesToCheck.some(name => name === normalizedCategoryName)) {
        return true;
      }
      
      // Partial match (for variations)
      if (namesToCheck.some(name => 
        name && (name.includes(normalizedCategoryName) || normalizedCategoryName.includes(name))
      )) {
        return true;
      }
      
      return false;
    }) || null;
  }

  // Helper function to reconstruct location segments from URL parts
  private static reconstructLocationSegments(
    locationParts: string[],
    locale: string
  ): import('../../../types/locationSegment').LocationSegment[] {
    const segments: import('../../../types/locationSegment').LocationSegment[] = [];
    
    // Decode URL-encoded parts first
    const decodedParts = locationParts.map(part => {
      try {
        return decodeURIComponent(part);
      } catch (error) {
        console.warn('Failed to decode URL part:', part, error);
        return part;
      }
    });
    
    // Check if first part is a special location type
    const firstPart = decodedParts[0];
    const specialLocationTypeId = this.getLocationTypeIdFromLabel(firstPart, locale);
    
    if (specialLocationTypeId !== null) {
      // Special location type handling
      decodedParts.forEach((part, index) => {
        const label = this.reconstructLabelFromSlug(part, locale);
        segments.push({
          id: `segment-${index}`,
          label,
          slug: locationParts[index] // Keep original encoded slug
        });
      });
    } else {
      // Regular location hierarchy (province/district/sub-district)
      decodedParts.forEach((part, index) => {
        const label = this.reconstructLabelFromSlug(part, locale);
        segments.push({
          id: `location-${index}`,
          label,
          slug: locationParts[index] // Keep original encoded slug
        });
      });
    }
    
    return segments;
  }

  // Helper function to get location type ID from label
  private static getLocationTypeIdFromLabel(label: string, locale: string): number | null {
    const normalizedLabel = label.toLowerCase();
    
    for (const [typeId, labels] of Object.entries(LOCATION_TYPE_LABELS)) {
      const typeLabels = labels as { th: string; en: string; cn: string };
      const labelsToCheck = [
        typeLabels.th.toLowerCase(),
        typeLabels.en.toLowerCase().replace(/\s+/g, '-'),
        typeLabels.cn.toLowerCase()
      ];
      
      if (labelsToCheck.some(l => l === normalizedLabel)) {
        return parseInt(typeId);
      }
    }
    
    return null;
  }

  // Helper function to reconstruct label from slug
  private static reconstructLabelFromSlug(
    slug: string,
    locale: string
  ): import('../../../types/locationSegment').LocalizedLabel {
    // If the input is already decoded Thai/Chinese text, use it as-is
    // Otherwise, convert slug back to readable format
    const isAlreadyDecoded = /[\u0E00-\u0E7F\u4E00-\u9FFF]/.test(slug);
    
    let readable: string;
    if (isAlreadyDecoded) {
      // Already decoded text (Thai/Chinese), use as-is
      readable = slug;
    } else {
      // Convert slug back to readable format
      readable = slug
        .replace(/-/g, ' ')
        .replace(/\b\w/g, l => l.toUpperCase());
    }
    
    return {
      th: readable,
      en: readable,
      cn: readable
    };
  }

  // Helper function to parse query parameters
  private static parseQueryParameters(
    queryParams: URLSearchParams,
    searchData: Partial<SearchData>
  ): void {
    // Price filters
    const startPrice = queryParams.get('startPrice');
    if (startPrice) {
      searchData.priceMin = parseInt(startPrice);
    }
    
    const endPrice = queryParams.get('endPrice');
    if (endPrice) {
      searchData.priceMax = parseInt(endPrice);
    }
    
    // Area filters
    const areaMin = queryParams.get('areaMin');
    if (areaMin) {
      searchData.areaMin = parseInt(areaMin);
    }
    
    const areaMax = queryParams.get('areaMax');
    if (areaMax) {
      searchData.areaMax = parseInt(areaMax);
    }
    
    // Additional filters
    const filters = [
      'bedrooms', 'bathrooms', 'quota', 'status', 'floor', 
      'view', 'direction', 'publishedBy', 'postedDate'
    ];
    
    filters.forEach(filter => {
      const value = queryParams.get(filter);
      if (value) {
        const key = `selected${filter.charAt(0).toUpperCase() + filter.slice(1)}` as keyof SearchData;
        (searchData as any)[key] = decodeURIComponent(value);
      }
    });
    
    // Dynamic filters
    const dynamicFilters: Record<number, number> = {};
    const apiQueryParams: Record<string, any> = {};
    
    Array.from(queryParams.entries()).forEach(([key, value]) => {
      if (key.startsWith('dynamicFilter_')) {
        const groupId = parseInt(key.replace('dynamicFilter_', ''));
        dynamicFilters[groupId] = parseInt(value);
      } else if (!['startPrice', 'endPrice', 'areaMin', 'areaMax', ...filters].includes(key)) {
        apiQueryParams[key] = value;
      }
    });
    
    if (Object.keys(dynamicFilters).length > 0) {
      searchData.selectedDynamicFilters = dynamicFilters;
    }
    
    if (Object.keys(apiQueryParams).length > 0) {
      searchData.apiQueryParams = apiQueryParams;
    }
  }
} 